import type { StyleSpecification } from 'maplibre-gl';

export function getMapLibreStyle(dark: boolean): StyleSpecification {
  const C = dark
    ? {
        bg: '#2A2B2F',
        shadow: '#0B0C0E',
        highlight: '#55585F',
        accentHS: '#1E1F22',
        park: '#2A3A30',
        parkOp: 0.42,
        water: '#1B2B3E',
        urban: '#323439',
        mwFill: '#776A50',
        mwCase: '#202124',
        prim: '#45484E',
        primCase: '#232427',
        bound: '#7B7194',
        route: '#3D95FF',
        routeCase: '#0B4FB0',
        halo: '#121417',
        altRoute: '#5E7FA8',
      }
    : {
        bg: '#F3EFE7',
        shadow: '#6E604C',
        highlight: '#FFFFFF',
        accentHS: '#9A8B74',
        park: '#D5E8C9',
        parkOp: 0.75,
        water: '#A9D3F2',
        urban: '#E9E5DE',
        mwFill: '#F5D58E',
        mwCase: '#E0BC6E',
        prim: '#FFFFFF',
        primCase: '#DDD6CA',
        bound: '#B4A7C8',
        route: '#1C7EF7',
        routeCase: '#0B5BD0',
        halo: '#FFFFFF',
        altRoute: '#8FB4E8',
      };

  const omt = 'openmaptiles';

  return {
    version: 8,
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources: {
      openmaptiles: {
        type: 'vector',
        url: 'https://tiles.openfreemap.org/planet',
      },
      dem: {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 11,
      },
    },
    layers: [
      {
        id: 'bg',
        type: 'background',
        paint: { 'background-color': C.bg },
      },
      {
        id: 'urban',
        type: 'fill',
        source: omt,
        'source-layer': 'landuse',
        filter: [
          'in',
          ['get', 'class'],
          ['literal', ['residential', 'suburb', 'neighbourhood', 'commercial', 'industrial', 'retail']],
        ],
        paint: { 'fill-color': C.urban, 'fill-opacity': 0.9 },
      },
      {
        id: 'wood',
        type: 'fill',
        source: omt,
        'source-layer': 'landcover',
        filter: ['==', ['get', 'class'], 'wood'],
        paint: { 'fill-color': C.park, 'fill-opacity': dark ? 0.3 : 0.45 },
      },
      {
        id: 'park',
        type: 'fill',
        source: omt,
        'source-layer': 'park',
        paint: { 'fill-color': C.park, 'fill-opacity': C.parkOp },
      },
      {
        id: 'hillshade',
        type: 'hillshade',
        source: 'dem',
        paint: {
          'hillshade-exaggeration': dark ? 0.32 : 0.38,
          'hillshade-shadow-color': C.shadow,
          'hillshade-highlight-color': C.highlight,
          'hillshade-accent-color': C.accentHS,
          'hillshade-illumination-direction': 315,
        },
      },
      {
        id: 'water',
        type: 'fill',
        source: omt,
        'source-layer': 'water',
        paint: { 'fill-color': C.water },
      },
      {
        id: 'bound-state',
        type: 'line',
        source: omt,
        'source-layer': 'boundary',
        filter: ['==', ['get', 'admin_level'], 4],
        paint: {
          'line-color': C.bound,
          'line-width': 1.1,
          'line-dasharray': [3, 2],
          'line-opacity': 0.9,
        },
      },
      {
        id: 'prim-case',
        type: 'line',
        source: omt,
        'source-layer': 'transportation',
        minzoom: 9,
        filter: ['in', ['get', 'class'], ['literal', ['trunk', 'primary']]],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': C.primCase, 'line-width': 1.4, 'line-opacity': dark ? 0.35 : 0.5 },
      },
      {
        id: 'prim',
        type: 'line',
        source: omt,
        'source-layer': 'transportation',
        minzoom: 9,
        filter: ['in', ['get', 'class'], ['literal', ['trunk', 'primary']]],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': C.prim, 'line-width': 0.8, 'line-opacity': dark ? 0.35 : 0.5 },
      },
      {
        id: 'mw-case',
        type: 'line',
        source: omt,
        'source-layer': 'transportation',
        filter: ['==', ['get', 'class'], 'motorway'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': C.mwCase, 'line-width': 2.4, 'line-opacity': dark ? 0.45 : 0.7 },
      },
      {
        id: 'mw',
        type: 'line',
        source: omt,
        'source-layer': 'transportation',
        filter: ['==', ['get', 'class'], 'motorway'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': C.mwFill, 'line-width': 1.4, 'line-opacity': dark ? 0.45 : 0.7 },
      },
    ],
  };
}
